import { Stack } from '@chakra-ui/react';
import Link from 'components/Link';
import { StatusTag } from 'components/StatusTag';
import { B2CMultiSelectDropDown } from 'components/Table/B2CMultiSelectDropdown';
import { SelectColumnFilter } from 'components/Table/Filter';
import { useUserContext } from 'context/UserContextProvider';
import useB2CServiceHistoriesRequestQuery from 'hooks/useB2CServiceHistoriesRequestQuery';
import { ContentCard } from 'layout/ContentCard';
import { isEqual, isNil, keys } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { DataTable } from '../../components/Table/DataTable';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { createDropdownPlainOptions } from '../../helpers/general';

const B2CServiceHistoryOverviewCard = () => {
  const { t } = useTranslation();

  const { makes } = useUserContext();

  const serviceHistoryCodes = {
    created: 'created',
    submitted: 'submitted',
    processing: 'processing',
    pending_payment: 'pending_payment',
    canceled_invalid_vin: 'canceled_invalid_vin',
    canceled_payment_failed: 'canceled_payment_failed',
    canceled_payment_overdue: 'canceled_payment_overdue',
    canceled_no_dsb: 'canceled_no_dsb',
    paid: 'paid',
    paid_expired: 'paid_expired',
  };

  const initialStatus = keys(serviceHistoryCodes).filter((code) => !['0'].includes(code));

  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: initialStatus || [],
    make: undefined,
  };

  const localStorageData = window.localStorage.getItem('b2c_service_history_requests_filters');
  const destringifiedData = JSON.parse(localStorageData!) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('b2c_service_history_requests_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [make, setMake] = useState(destringifiedData?.make || initialFilters?.make);
  const [filtersSelected, setFiltersSelected] = useState(false);

  const serviceHistoryQuery = useB2CServiceHistoriesRequestQuery({
    searchString,
    page,
    itemsPerPage,
    status: status.join(','),
    make_id: make,
  });

  // @ts-ignore
  const currentPage = serviceHistoryQuery.data?.meta?.current_page - 1;

  useEffect(() => {
    setStatus(initialStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1 && isEqual(initialStatus, status) && isNil(make);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, make, status]);

  useEffect(() => {
    setFiltersSelected(!isNil(make) || status?.length !== 0);
    window.localStorage.setItem(
      'b2c_service_history_requests_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        itemsPerPage: itemsPerPage,
        make: make,
      }),
    );
  }, [searchString, status, itemsPerPage, make]);

  const allStatusSelected = status.length === Object.keys(serviceHistoryCodes).length;

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'user_readable_id',
        //@ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link data-test-id={'shr-' + row?.index} to={`/app/op/b2c-service-history-request/${row.original.id}`}>
              {row.original.user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () => B2CMultiSelectDropDown(status, setStatus, 'b2c_service_history_request', serviceHistoryCodes, allStatusSelected, t('forms:status.label')),
        // @ts-ignore
        Cell: ({ value }: { value: string }) => <StatusTag resourceKey="b2c_service_history_request" currentStatus={value} isCell />,
      },
      {
        Header: 'Verification',
        accessor: 'verification?.id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const displayLinkPlaceholder = row.original?.verification?.id ? (
            <Link to={`/app/op/b2c-verification-request/${row.original?.verification?.id}`}>{row.original?.verification?.user_readable_id}</Link>
          ) : (
            '-'
          );
          return displayLinkPlaceholder;
        },
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (props) => {
          return props.value ?? '-';
        },
      },
      {
        Header: t('forms:created_at.label', { date: '' }),
        accessor: (row: { created_at: string }) => dateTimeTransformer(row.created_at) ?? '-',
      },
      {
        Header: t('forms:make.label'),
        accessor: (row: any) => row?.make,
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: make,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: makes,
              labelColumn: 'name',
              valueColumn: 'id',
            }),
            onChange: setMake,
          }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceHistoryCodes, initialStatus, allStatusSelected, searchString, status, itemsPerPage, page],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('b2c_service_history_request:overview.header')}
      data-test-id="service-history-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setMake(initialFilters?.make);
              window.localStorage.setItem('b2c_service_history_requests_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="service-history-table"
            data={serviceHistoryQuery.data?.data || []}
            globalFilter={searchString}
            filtersSelected={filtersSelected}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('b2c_service_history_request:overview.search_placeholder')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            total={serviceHistoryQuery.data?.meta?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={serviceHistoryQuery.isFetching || serviceHistoryQuery.isLoading}
            isMoreThenOnePage={
              // @ts-ignore
              !initialLoad || Boolean(serviceHistoryQuery.data?.meta?.next_page_url) || currentPage > 0
            }
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default B2CServiceHistoryOverviewCard;
